import React, { useState } from 'react';

import FilterChip from '../ui/FilterChip';
import DocumentCard from '../ui/DocumentCard';

/**
 * RightSidebarDesktop Component
 * 
 * Desktop version of the right sidebar for document management.
 * Exact copy of original RightSidebar with max-lg:hidden removed.
 */
const RightSidebarDesktop = ({ hidden, activeView, onDocumentClick, onAddDocument, documents = [], theme = 'dark', activeFilter = 'all', onFilterChange, onDeleteDocument }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Hide if hidden prop is true or for Chat view
  if (hidden || activeView === 'chat') {
    return null;
  }

  const filters = ['All', 'Tax', 'Receipt', 'Statement'];

  // Filter documents by active view and map to UI fields
  const filteredDocs = documents
    .filter(doc => doc.app === activeView)
    .map(d => ({
      ...d,
      title: d.title || d.name,
      filename: d.filename || d.name,
      badge: d.type || d.category || d.badge || (activeView === 'taxper' ? 'tax' : 'ready'),
      loading: false
    }));

  // Filter based on active filter and search query
  const searchFilteredDocs = filteredDocs.filter(doc => {
    const matchesFilter = activeFilter === 'all' || doc.badge.toLowerCase() === activeFilter;
    const matchesSearch = !searchQuery.trim() ||
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <aside
      id="rightSidebar"
      className="right-sidebar fixed right-0 top-0 w-[340px] h-screen flex-col z-[50]"
      style={{
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        borderLeft: theme === 'dark'
          ? '0.5px solid rgba(255, 255, 255, 0.08)'
          : '0.5px solid rgba(71, 85, 105, 0.5)',
        boxShadow: theme === 'light'
          ? '0 0 4px rgba(15, 23, 42, 0.08), inset -1px 0 2px rgba(0, 0, 0, 0.02)'
          : 'none',
        display: 'flex',
      }}
      data-theme={theme}
    >
      {/* Right Sidebar Header */}
      <div
        className="right-sidebar-header"
        style={{
          padding: 'calc(60px + 6px) 12px 12px',
          borderBottom: theme === 'dark'
            ? '0.5px solid rgba(255, 255, 255, 0.08)'
            : '0.5px solid rgba(71, 85, 105, 0.4)',
          boxShadow: theme === 'light'
            ? '0 2px 4px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'
            : 'none',
        }}
      >
        <h2
          className="m-0 mb-1 text-[20px] font-semibold"
          style={{
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
          }}
        >
          Documents
        </h2>
        <p
          className="m-0 mb-3 text-[12px]"
          style={{
            color: theme === 'dark' ? '#888888' : '#1e293b',
          }}
        >
          {activeView === 'taxper' ? 'Tax documents and receipts' : 'Contracts and legal docs'}
        </p>

        {/* Search Input */}
        <input
          type="search"
          className="doc-search w-full h-[42px] px-3 rounded-2xl border outline-none text-[13px] font-inherit mb-4 transition-all duration-300"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            border: theme === 'dark'
              ? '0.5px solid rgba(255, 255, 255, 0.2)'
              : '0.5px solid rgba(71, 85, 105, 0.5)',
            background: theme === 'dark'
              ? 'rgba(255, 255, 255, 0.12)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
            WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
            color: theme === 'dark' ? '#ffffff' : '#0f172a',
            boxShadow: theme === 'dark'
              ? 'inset 0 2px 4px rgba(0, 0, 0, 0.4)'
              : '0 1px 3px rgba(15, 23, 42, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)',
          }}
          onFocus={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.6)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.98)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)';
            }
          }}
          onBlur={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.4)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.5)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.08), inset 0 1px 2px rgba(0, 0, 0, 0.02)';
            }
          }}
        />

        {/* Filter Chips */}
        <div className="filter-chips flex gap-1.5 flex-wrap">
          {filters.map((filter) => {
            const filterKey = filter.toLowerCase();
            const isActive = activeFilter === filterKey;
            return (
              <FilterChip
                key={filter}
                label={filter}
                isActive={isActive}
                onClick={() => onFilterChange(filterKey)}
                theme={theme}
              />
            );
          })}
        </div>
      </div>

      {/* Document List */}
      <ul
        id="taxperSidebarScroll"
        className="doc-list flex-1 overflow-y-auto p-3 flex flex-col gap-1.5"
      >
        {searchFilteredDocs.map((doc) => (
          <li key={doc.id}>
            <DocumentCard
              document={doc}
              onClick={onDocumentClick}
              theme={theme}
              showSkeleton={doc.loading}
              onDelete={(doc) => onDeleteDocument?.(doc.id)}
            />
          </li>
        ))}
      </ul>

      {/* Right Sidebar Footer */}
      <div
        className="right-sidebar-footer p-3"
        style={{
          borderTop: theme === 'dark'
            ? '0.5px solid rgba(255, 255, 255, 0.08)'
            : '0.5px solid rgba(71, 85, 105, 0.4)',
          boxShadow: theme === 'light'
            ? '0 -2px 4px rgba(15, 23, 42, 0.06), 0 -1px 2px rgba(0, 0, 0, 0.04)'
            : 'none',
        }}
      >
        <div
          className="add-doc-area border border-dashed rounded-2xl p-5 flex items-center justify-center cursor-pointer transition-all duration-300 bg-transparent active:scale-[0.98]"
          onClick={onAddDocument}
          style={{
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(71, 85, 105, 0.4)',
            boxShadow: theme === 'light'
              ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 0 1px rgba(0, 0, 0, 0.05)'
              : 'none',
          }}
          onMouseEnter={(e) => {
            if (theme === 'dark') {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
            } else {
              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.6)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(71, 85, 105, 0.4)';
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.boxShadow = theme === 'light'
              ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 0 1px rgba(0, 0, 0, 0.05)'
              : 'none';
          }}
        >
          <button
            className="add-doc-btn w-14 h-14 rounded-full border flex items-center justify-center transition-all duration-300 active:scale-[0.98]"
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(135deg, rgba(40, 40, 40, 0.9), rgba(20, 20, 20, 0.95))'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: theme === 'light' ? 'blur(20px)' : 'none',
              WebkitBackdropFilter: theme === 'light' ? 'blur(20px)' : 'none',
              border: theme === 'dark'
                ? '0.5px solid rgba(255, 255, 255, 0.1)'
                : '0.5px solid rgba(71, 85, 105, 0.5)',
              color: theme === 'dark' ? '#ffffff' : '#0f172a',
              boxShadow: theme === 'light'
                ? '0 2px 4px rgba(15, 23, 42, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)'
                : 'none',
            }}
            aria-label="Add document"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2V26M2 14H26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default RightSidebarDesktop;

